import { useEffect, useRef, useState } from "react";
import { LogOut, ShoppingCart } from "lucide-react";
import { KisayolEkle } from "@/components/bayi/KisayolEkle";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { GlassCard, fmtMoney } from "@/components/erp";
import { BayiUrunler } from "@/components/bayi/BayiUrunler";
import { BayiAracSec } from "@/components/bayi/BayiAracSec";
import { BayiSepet } from "@/components/bayi/BayiSepet";
import { BayiSiparisler } from "@/components/bayi/BayiSiparisler";
import { BayiHizli } from "@/components/bayi/BayiHizli";
import { BayiFavoriler } from "@/components/bayi/BayiFavoriler";
import { BayiDuyurular } from "@/components/bayi/BayiDuyurular";
import { BayiTeklifler } from "@/components/bayi/BayiTeklifler";
import { BayiEkstre, BayiVadeUyari } from "@/components/bayi/BayiEkstre";
import { BayiMutabakat, BayiFaturalar, MutabakatUyari } from "@/components/bayi/BayiGuvenlik";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";
import { useCevrimici, katalogYenile, bekleyen, bekleyenKaydet, agHatasi } from "@/lib/katalog";
import { WifiOff, CloudUpload } from "lucide-react";

const Bant = ({ cevrimici, b, onIptal, gonderimde }) => {
  if (cevrimici && !b) return null;
  return (
    <div className="rounded-2xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm flex flex-wrap items-center gap-3" data-testid="bayi-cevrimdisi-bant">
      {cevrimici ? <CloudUpload className="w-4 h-4 text-amber-300" /> : <WifiOff className="w-4 h-4 text-amber-300" />}
      <span className="flex-1 min-w-[200px]">
        {!cevrimici && "İnternet bağlantısı yok. Ürünler telefona kaydedilen listeden gösteriliyor; fiyatlar son bilinen fiyatlardır. "}
        {b && <span data-testid="bayi-bekleyen">{b.kalemler.length} kalemli siparişiniz {cevrimici ? "gönderiliyor…" : "bağlantı gelince otomatik gönderilecek."}</span>}
      </span>
      {b && !gonderimde && <button data-testid="bayi-bekleyen-iptal" onClick={onIptal} className="text-xs px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15">Sepete geri al</button>}
    </div>
  );
};

const Ozet = ({ o, salt }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="bayi-ozet">
    {[["Bakiye (TL)", fmtMoney(o.risk.bakiye), "bayi-bakiye"], ...(salt ? [] : [["Risk limiti (TL)", o.risk.limit ? fmtMoney(o.risk.limit) : "Tanımsız", "bayi-limit"],
      ["Kullanılabilir limit (TL)", o.risk.limit ? fmtMoney(o.risk.kalan) : "—", "bayi-kalan"]]), ["Temsilciniz", o.temsilci || "Firma", "bayi-temsilci"]].map(([l, v, t]) => (
      <GlassCard key={l} className="p-4"><div className="text-[11px] uppercase tracking-wider text-muted-foreground">{l}</div><div className="font-mono font-semibold mt-1 truncate" data-testid={t}>{v}</div></GlassCard>
    ))}
  </div>
);

export default function BayiPortal() {
  const { user, logout } = useAuth();
  const anahtar = `bayi-sepet-${user.id}`;
  const [o, setO] = useState(null);
  const [tab, setTab] = useState("urunler");
  const [acik, setAcik] = useState(false);
  const [sepet, setSepet] = useState(() => { try { return JSON.parse(localStorage.getItem(anahtar)) || []; } catch { return []; } });
  const [yenile, setYenile] = useState(0);
  const [hata, setHata] = useState("");
  const cevrimici = useCevrimici();
  const [bek, setBek] = useState(() => bekleyen(user.id));
  const gidiyor = useRef(false);
  const [deneme, setDeneme] = useState(0);
  const [gonderimde, setGonderimde] = useState(false);
  const [mutBek, setMutBek] = useState([]);
  const mutYukle = () => api.get("/bayi/mutabakatlar").then((r) => setMutBek(r.data.filter((m) => m.durum === "bekliyor"))).catch(() => {});
  useEffect(() => { mutYukle(); }, []);
  const salt = o?.cari?.tip === "tedarikci";
  useEffect(() => { if (salt) { setTab((t) => (["faturalar", "mutabakat", "ekstre"].includes(t) ? t : "faturalar")); } }, [salt]);
  useEffect(() => { if (cevrimici && !salt && o) { katalogYenile(user.id).catch(() => {}); } }, [cevrimici, user.id, salt, o]);
  useEffect(() => {
    if (!cevrimici || !bek || gidiyor.current) { return; }
    gidiyor.current = true;
    setGonderimde(true);
    api.post("/bayi/siparis", { kalemler: bek.kalemler.map((k) => ({ urun_id: k.id, miktar: k.miktar })), notlar: bek.notlar, istemci_no: bek.istemci_no })
      .then(({ data }) => {
        toast.success(`Çevrimdışı siparişiniz gönderildi: ${data.no}${data.durum === "onay_bekliyor" ? " (firma onayına gönderildi)" : ""}`);
        bekleyenKaydet(user.id, null); setBek(null); setYenile((n) => n + 1);
      })
      .catch((e) => {
        if (agHatasi(e) || e.response?.status === 409) { setTimeout(() => setDeneme((n) => n + 1), 8000); return; }
        toast.error(`Çevrimdışı sipariş gönderilemedi: ${errMsg(e)}. Ürünler sepete geri alındı.`);
        geriAl();
      })
      .finally(() => { gidiyor.current = false; setGonderimde(false); });
  }, [cevrimici, bek, deneme]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { localStorage.setItem(anahtar, JSON.stringify(sepet)); }, [sepet, anahtar]);
  useEffect(() => {
    api.get("/bayi/ozet").then((r) => { setO(r.data); localStorage.setItem(`bayi-ozet-${user.id}`, JSON.stringify(r.data)); })
      .catch((e) => { const c = agHatasi(e) && localStorage.getItem(`bayi-ozet-${user.id}`); if (c) setO(JSON.parse(c)); else setHata(errMsg(e)); });
  }, [yenile, user.id]);
  const ekle = (u, m) => setSepet((s) => (s.some((x) => x.id === u.id) ? s.map((x) => (x.id === u.id ? { ...x, miktar: Number(x.miktar) + m } : x))
    : [...s, { id: u.id, kod: u.kod, ad: u.ad, miktar: m }]));
  const ekleCoklu = (liste) => liste.forEach((u) => ekle(u, u.miktar));
  const geriAl = () => { const b = bekleyen(user.id); if (b) ekleCoklu(b.kalemler); bekleyenKaydet(user.id, null); setBek(null); };
  const kuyruk = () => { setSepet([]); setAcik(false); setBek(bekleyen(user.id)); };
  const gonderildi = () => { setSepet([]); setAcik(false); setTab("siparisler"); setYenile((n) => n + 1); };
  const sepetPanel = <BayiSepet sepet={sepet} setSepet={setSepet} onGonderildi={gonderildi} uid={user.id} cevrimici={cevrimici} onKuyruk={kuyruk} />;
  return (
    <div className="min-h-screen app-bg" data-testid="bayi-portal">
      <header className="sticky top-0 z-30 glass border-b border-white/5">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-8 h-16 flex items-center gap-4">
          <div className="w-9 h-9 rounded-xl grad-bg flex items-center justify-center font-head font-bold text-white">{(o?.firma.kisa_ad || o?.firma.unvan || "B").charAt(0)}</div>
          <div className="min-w-0"><div className="font-head font-semibold truncate">{o?.firma.kisa_ad || o?.firma.unvan || "Bayi Portalı"}</div>
            <div className="text-xs text-muted-foreground truncate" data-testid="bayi-unvan">{o?.cari.unvan}</div></div>
          {!salt && <button data-testid="bayi-sepet-ac" onClick={() => setAcik(true)} className="ml-auto lg:hidden relative p-2.5 rounded-full bg-white/5"><ShoppingCart className="w-5 h-5" />
            {sepet.length > 0 && <span className="absolute -top-1 -right-1 text-[10px] w-5 h-5 rounded-full grad-bg text-white flex items-center justify-center">{sepet.length}</span>}</button>}
          <div className={salt ? "ml-auto" : "lg:ml-auto"}><KisayolEkle /></div>
          <button data-testid="bayi-cikis" onClick={logout} className="p-2.5 rounded-full text-muted-foreground hover:text-white hover:bg-white/5"><LogOut className="w-5 h-5" /></button>
        </div>
      </header>
      <main className="max-w-[1500px] mx-auto px-4 sm:px-8 py-8 space-y-6">
        <div><div className="text-[11px] tracking-[0.3em] uppercase text-muted-foreground">{salt ? "Tedarikçi portalı" : "Bayi portalı"}</div>
          <h1 className="font-head text-3xl sm:text-4xl font-bold mt-1">Hoş geldiniz, {user.name}</h1></div>
        {hata && cevrimici && <p className="text-sm text-red-300" data-testid="bayi-hata">{hata}. Lütfen firmayla iletişime geçin.</p>}
        <MutabakatUyari liste={mutBek} onAc={() => setTab("mutabakat")} />
        {!salt && <Bant cevrimici={cevrimici} b={bek} onIptal={geriAl} gonderimde={gonderimde} />}
        {o && !salt && <BayiVadeUyari onEkstre={() => setTab("ekstre")} />}
        {o && !salt && <BayiDuyurular userId={user.id} onEkle={(u, m) => { ekle(u, m); toast.success(`${u.kod} sepete eklendi`); }} />}
        {o && <Ozet o={o} salt={salt} />}
        {o?.kur_eksik && !salt && <p className="text-sm text-amber-300" data-testid="bayi-kur-eksik">Döviz kuru henüz tanımlanmadı; dövizli ürünlerin fiyatı görünmüyor ve sepete eklenemiyor. Lütfen firmayla iletişime geçin.</p>}
        <div className={salt ? "" : "grid lg:grid-cols-[minmax(0,1fr)_380px] gap-6 items-start"}>
          <Tabs value={tab} onValueChange={setTab} className="min-w-0">
            <TabsList className="bg-white/5 mb-4 flex-wrap h-auto">
              {!salt && <>
              <TabsTrigger value="urunler" data-testid="bayi-tab-urunler">Ürünler & Sipariş</TabsTrigger>
              <TabsTrigger value="arac" data-testid="bayi-tab-arac">Aracıma göre bul</TabsTrigger>
              <TabsTrigger value="favoriler" data-testid="bayi-tab-favoriler">Favorilerim</TabsTrigger>
              <TabsTrigger value="hizli" data-testid="bayi-tab-hizli">Hızlı Sipariş</TabsTrigger>
              <TabsTrigger value="siparisler" data-testid="bayi-tab-siparisler">Siparişlerim</TabsTrigger>
              <TabsTrigger value="teklifler" data-testid="bayi-tab-teklifler">Tekliflerim</TabsTrigger></>}
              <TabsTrigger value="ekstre" data-testid="bayi-tab-ekstre">Ekstrem</TabsTrigger>
              <TabsTrigger value="mutabakat" data-testid="bayi-tab-mutabakat">Mutabakat{mutBek.length > 0 && <span className="ml-1.5 text-[10px] px-1.5 rounded-full bg-amber-400 text-black" data-testid="bayi-mutabakat-sayi">{mutBek.length}</span>}</TabsTrigger>
              {["tedarikci", "her_ikisi"].includes(o?.cari?.tip) && <TabsTrigger value="faturalar" data-testid="bayi-tab-faturalar">Faturalarım (tedarikçi)</TabsTrigger>}
            </TabsList>
            <TabsContent value="urunler">{o && !salt && <BayiUrunler pb={o.cari.para_birimi} onEkle={ekle} />}</TabsContent>
            <TabsContent value="arac">{o && tab === "arac" && <BayiAracSec pb={o.cari.para_birimi} onEkle={ekle} />}</TabsContent>
            <TabsContent value="favoriler">{o && tab === "favoriler" && <BayiFavoriler pb={o.cari.para_birimi} onEkle={ekle} />}</TabsContent>
            <TabsContent value="hizli">{!salt && <BayiHizli onEkle={ekleCoklu} />}</TabsContent>
            <TabsContent value="siparisler">{!salt && <BayiSiparisler key={yenile} onTekrar={(l) => { ekleCoklu(l); setTab("urunler"); if (window.innerWidth < 1024) setAcik(true); }} />}</TabsContent>
            <TabsContent value="teklifler">{tab === "teklifler" && <BayiTeklifler />}</TabsContent>
            <TabsContent value="ekstre">{tab === "ekstre" && <BayiEkstre />}</TabsContent>
            <TabsContent value="mutabakat">{tab === "mutabakat" && <BayiMutabakat onDegisti={mutYukle} />}</TabsContent>
            <TabsContent value="faturalar">{tab === "faturalar" && <BayiFaturalar />}</TabsContent>
          </Tabs>
          {!salt && <GlassCard className="hidden lg:block p-5 sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto erp-scroll">
            <div className="font-head font-semibold mb-1 flex items-center gap-2"><ShoppingCart className="w-4 h-4" />Sepet</div>
            {o && <p className="text-[11px] text-muted-foreground">Onay kuralı: {o.onay_kurali}</p>}
            {!acik && sepetPanel}
          </GlassCard>}
        </div>
      </main>
      <Sheet open={acik} onOpenChange={setAcik}>
        <SheetContent side="bottom" className="glass max-h-[88vh] overflow-y-auto" data-testid="bayi-sepet-sheet">
          <SheetHeader><SheetTitle>Sepet</SheetTitle></SheetHeader>
          {acik && sepetPanel}
        </SheetContent>
      </Sheet>
    </div>
  );
}
